import { format } from 'date-fns';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Avatar } from '../catalyst/avatar';
import { ChevronDownIcon, ChevronUpIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { Trendline } from '../../pages/Settings/Playbooks/PlaybookTasks';
import { SimpleTooltip } from '../Tooltip/Tooltip';
import { VisibilityButtonWithMutation } from '../../components/VisibilityButton';
import { Button } from '../catalyst/button';
import clsx from 'clsx';
import TaskDropdownMenu from '../../pages/ProjectDetail/tabs/TaskDropdownMenu';
import StatusBadge from '../StatusBadge/StatusBadge';
import { getTaskStatus } from '../../pages/TasksPage';
import UserDetails from '../../pages/MutualActionPlan/TaskDetails/UserDetails';

function rangeFormat(start, end) {
  const startDate = new Date(start);
  const endDate = new Date(end);

  if (
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth()
  ) {
    if (startDate.getDate() === endDate.getDate()) {
      return format(endDate, 'd MMM');
    }

    return `${startDate.getDate()} - ${format(endDate, 'd MMM')}`;
  }

  return `${format(startDate, 'd MMM')} - ${format(endDate, 'd MMM')}`;
}

function TaskAssignee({ assignee, type }) {
  if (!assignee) {
    return (
      <SimpleTooltip label={`No ${type} assignee yet`}>
        <UserCircleIcon className="size-6" />
      </SimpleTooltip>
    );
  }

  return (
    <SimpleTooltip label={<UserDetails {...assignee} />}>
      <Avatar
        className="size-6"
        src={assignee.avatar}
        initials={assignee.initials}
      />
    </SimpleTooltip>
  );
}

export default function TaskOverview({
  task,
  toggleGroup,
  isExpanded,
  archiveTaskMutation,
  completeTaskMutation,
  reopenTaskMutation,
  cloneTaskMutation,
  toggleVisibilityMutation,
  completedInThePastMutation,
  onAddTaskToGroupClick,
  onMoveTaskClick,
}) {
  const location = useLocation();
  const { internalAssignee, externalAssignee, isGroup } = task;
  const [searchParams] = useSearchParams();
  const navbars = searchParams.get('navbars');
  let pathPrefix = isGroup ? 'task-groups' : 'tasks';
  if (!location.pathname.includes('timeline')) {
    pathPrefix = `timeline/${pathPrefix}`;
  }

  return (
    <div className="flex justify-between items-center w-full">
      <div className={clsx('flex items-center overflow-hidden', !isGroup && 'pl-11')}>
        {isGroup && (
          <Button
            plain
            onClick={() => toggleGroup(task.id, !isExpanded)}
            className="mr-2"
          >
            {isExpanded ? (
              <ChevronUpIcon className="size-4" />
            ) : (
              <ChevronDownIcon className="size-4" />
            )}
          </Button>
        )}
        <div className="overflow-hidden text-ellipsis text-nowrap mr-6">
          <Link
            to={`${pathPrefix}/${task.id}${navbars ? `?navbars=${navbars}` : ''}`}
            className="font-bold text-blue-600 hover:underline overflow-hidden text-ellipsis text-nowrap"
          >
            {task.name}
          </Link>
          <p className="overflow-hidden text-ellipsis text-nowrap">
            {task.taskType?.label}
            {task.isGroup ? 'Group' : ''}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-x-4">
        <div className="text-nowrap text-right">
          {task.isGroup || !task.completed ? (
            <SimpleTooltip
              type="transparent"
              label={<StatusBadge status={getTaskStatus(task)} />}
            >
              {rangeFormat(task.startDate, task.endDate)}
            </SimpleTooltip>
          ) : (
            <SimpleTooltip label={`Completed on ${format(new Date(task.endDate), 'd MMM')}`}>
              <StatusBadge status="completed" />
            </SimpleTooltip>
          )}
        </div>
        {!isGroup && (
          <div className="flex items-center justify-center">
            <TaskAssignee
              assignee={internalAssignee}
              type="internal"
            />
            <TaskAssignee
              assignee={externalAssignee}
              type="external"
            />
          </div>
        )}
        {!isGroup && (
          <SimpleTooltip label={task.clientVisible ? 'Visible to clients' : 'Hidden from clients'}>
            <VisibilityButtonWithMutation
              cardId={task.id}
              clientVisible={task.clientVisible}
              side={task.side}
              toggleVisibilityMutation={toggleVisibilityMutation}
              disabled={task.completed}
            />
          </SimpleTooltip>
        )}
        {!isGroup && (
          <SimpleTooltip
            label={
              <div>
                <p>
                  Average completion time: {task.taskType?.averageCompletionTime} day
                  {task.taskType?.averageCompletionTime > 1 ? 's' : ''}
                </p>
                <p>
                  Last completion time: {task.taskType?.lastCompletionTime} day
                  {task.taskType?.lastCompletionTime > 1 ? 's' : ''}
                </p>
              </div>
            }
          >
            <Trendline row={task} />
          </SimpleTooltip>
        )}
        <TaskDropdownMenu
          task={task}
          reopenTaskMutation={reopenTaskMutation}
          completeTaskMutation={completeTaskMutation}
          completedInThePastMutation={completedInThePastMutation}
          cloneTaskMutation={cloneTaskMutation}
          archiveTaskMutation={archiveTaskMutation}
          onAddTaskToGroupClick={onAddTaskToGroupClick}
          onMoveTaskClick={onMoveTaskClick}
        />
      </div>
    </div>
  );
}
